import {createBrowserRouter, RouterProvider} from 'react-router-dom'
import PublicLayout from './layouts/PublicLayout'
import RequireAuth from './auth/RequireAuth'
import LandingPage from './pages/LandingPage'
import PricingPage from './pages/PricingPage'
import FeaturesPage from './pages/FeaturesPage'
import IconGeneratorPage from './pages/IconGeneratorPage'
import QrGeneratorPage from './pages/QrGeneratorPage'
import ImageConverterPage from './pages/ImageConverterPage'
import CreativeAssetsPage from './pages/CreativeAssetsPage'
import VideoToGifPage from './pages/VideoToGifPage'
import TemplatesPage from './pages/TemplatesPage'
import LoginPage from './pages/LoginPage'
import VerifyMagicLinkPage from './pages/VerifyMagicLinkPage'
import ProjectsPage from './pages/ProjectsPage'
import RenderHistoryPage from './pages/RenderHistoryPage'
import AccountPage from './pages/AccountPage'
import EditorRoute from './pages/EditorRoute'
import FreeEditorRoute from './pages/FreeEditorRoute'
import AboutPage from './pages/AboutPage'
import ContactPage from './pages/ContactPage'
import FaqPage from './pages/FaqPage'
import TermsPage from './pages/TermsPage'
import PrivacyPage from './pages/PrivacyPage'
import CookiesPage from './pages/CookiesPage'
import RefundPage from './pages/RefundPage'
import ImprintPage from './pages/ImprintPage'
import UnsubscribePage from './pages/UnsubscribePage'
import SorryPage from './pages/SorryPage'
import RouterErrorElement from './components/errorBoundary/RouterErrorElement'
import ComparisonPage from './components/seo/ComparisonPage'
import GuideArticle from './components/seo/GuideArticle'
import CsgArticlePage from './components/seo/CsgArticlePage'
import GuidesIndexPage from './components/seo/GuidesIndexPage'
import ToolsPage from './pages/ToolsPage'
import {COMPARISONS} from './components/seo/comparisonData'
import {GUIDES} from './components/seo/guideData'

const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    errorElement: <RouterErrorElement />,
    children: [
      {path: '/', element: <LandingPage />},
      {path: '/pricing', element: <PricingPage />},
      {path: '/features', element: <FeaturesPage />},
      {path: '/icon-generator', element: <IconGeneratorPage />},
      {path: '/qr-generator', element: <QrGeneratorPage />},
      {path: '/image-converter', element: <ImageConverterPage />},
      {path: '/app-store-creative-assets', element: <CreativeAssetsPage />},
      {path: '/video-to-gif', element: <VideoToGifPage />},
      {path: '/projects', element: <TemplatesPage />},
      {path: '/login', element: <LoginPage />},
      {path: '/login/verify', element: <VerifyMagicLinkPage />},
      {
        path: '/my-projects',
        element: (
          <RequireAuth>
            <ProjectsPage />
          </RequireAuth>
        ),
      },
      {
        path: '/render-history',
        element: (
          <RequireAuth>
            <RenderHistoryPage />
          </RequireAuth>
        ),
      },
      {
        path: '/account',
        element: (
          <RequireAuth>
            <AccountPage />
          </RequireAuth>
        ),
      },
      {path: '/about', element: <AboutPage />},
      {path: '/contact', element: <ContactPage />},
      {path: '/faq', element: <FaqPage />},
      {path: '/terms', element: <TermsPage />},
      {path: '/privacy', element: <PrivacyPage />},
      {path: '/cookies', element: <CookiesPage />},
      {path: '/refund', element: <RefundPage />},
      {path: '/imprint', element: <ImprintPage />},
      {path: '/unsubscribe/:token', element: <UnsubscribePage />},
      // SEO marketing pages (prerendered to static HTML at build time)
      {path: '/guides', element: <GuidesIndexPage />},
      {path: '/tools', element: <ToolsPage />},
      {path: '/alternatives/appmockup', element: <ComparisonPage data={COMPARISONS.appmockup} />},
      {path: '/vs/rotato', element: <ComparisonPage data={COMPARISONS.rotato} />},
      {path: '/alternatives/previewed', element: <ComparisonPage data={COMPARISONS.previewed} />},
      {path: '/vs/screenshots-pro', element: <ComparisonPage data={COMPARISONS.screenshotsPro} />},
      {path: '/alternatives/screenhance', element: <ComparisonPage data={COMPARISONS.screenhance} />},
      {path: '/alternatives/applaunchpad', element: <ComparisonPage data={COMPARISONS.applaunchpad} />},
      {path: '/alternatives/appscreens', element: <ComparisonPage data={COMPARISONS.appscreens} />},
      {path: '/alternatives/screenfast', element: <ComparisonPage data={COMPARISONS.screenfast} />},
      {path: '/alternatives/butterkit', element: <ComparisonPage data={COMPARISONS.butterkit} />},
      {path: '/alternatives/appdrift', element: <ComparisonPage data={COMPARISONS.appdrift} />},
      {
        path: '/alternatives/appscreenshotstudio',
        element: <ComparisonPage data={COMPARISONS.appScreenshotStudio} />,
      },
      {path: '/alternatives/screenshotwhale', element: <ComparisonPage data={COMPARISONS.screenshotWhale} />},
      {path: '/guides/app-store-screenshot-sizes', element: <GuideArticle data={GUIDES.appStoreSizes} />},
      {path: '/guides/google-play-feature-graphic', element: <GuideArticle data={GUIDES.playFeatureGraphic} />},
      {path: '/guides/figma-app-store-screenshots', element: <GuideArticle data={GUIDES.figmaWorkflow} />},
      {path: '/guides/how-to-make-app-store-screenshots', element: <GuideArticle data={GUIDES.howToMake} />},
      {path: '/guides/app-store-screenshot-best-practices', element: <GuideArticle data={GUIDES.bestPractices} />},
      {path: '/guides/how-we-build-3d-device-mockups', element: <CsgArticlePage />},
      {
        path: '/guides/iphone-18-iphone-ultra-app-store-screenshots',
        element: <GuideArticle data={GUIDES.foldableScreenshots} />,
      },
      {
        path: '/guides/apple-september-2026-lineup-app-store-screenshots',
        element: <GuideArticle data={GUIDES.appleSeptember2026} />,
      },
      {
        path: '/guides/localized-app-store-screenshots',
        element: <GuideArticle data={GUIDES.localizedScreenshots} />,
      },
      {
        path: '/guides/screenshot-case-study-80-percent-more-downloads',
        element: <GuideArticle data={GUIDES.screenshotCaseStudy} />,
      },
      {
        path: '/guides/free-image-converter-video-to-gif',
        element: <GuideArticle data={GUIDES.freeTools} />,
      },
      {
        path: '/guides/app-store-screenshot-rejection-reasons',
        element: <GuideArticle data={GUIDES.screenshotRejections} />,
      },
      {
        path: '/guides/app-store-icon-guidelines',
        element: <GuideArticle data={GUIDES.appStoreIconGuidelines} />,
      },
      {
        path: '/guides/google-play-screenshot-requirements',
        element: <GuideArticle data={GUIDES.playScreenshotRequirements} />,
      },
      {
        path: '/guides/app-store-header-search-results-assets',
        element: <GuideArticle data={GUIDES.appStoreCreativeAssets} />,
      },
      {
        path: '/guides/panoramic-app-store-screenshots',
        element: <GuideArticle data={GUIDES.panoramicScreenshots} />,
      },
      {
        path: '/guides/app-store-assets',
        element: <GuideArticle data={GUIDES.appStoreAssets} />,
      },
      {
        path: '/guides/app-store-screenshot-ab-testing',
        element: <GuideArticle data={GUIDES.screenshotAbTesting} />,
      },
    ],
  },
  {path: '/free', element: <FreeEditorRoute />},
  {
    path: '/editor/new/:projectSlug?',
    element: (
      <RequireAuth>
        <EditorRoute />
      </RequireAuth>
    ),
  },
  {
    path: '/editor/:projectId',
    element: (
      <RequireAuth>
        <EditorRoute />
      </RequireAuth>
    ),
  },
  {path: '/error', element: <SorryPage />},
  {path: '*', element: <SorryPage variant="notFound" />},
])

const App = () => <RouterProvider router={router} />

export default App
